// Add-Device wizard — point IOSentra at a real NVR/DVR/VMS/camera (or the
// no-hardware video-demo driver) and have its channels become real devices.
//
// Flow: pick a driver -> enter connection (host/port/credentials) -> Test ->
// Create the instance (credentials encrypted server-side) + Start (the driver
// connects and auto-creates device rows) -> Discover shows what was added.

import { useMemo, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  X, Check, Loader2, Server, MapPin, Search, ChevronLeft,
  Video, MonitorPlay, HardDrive, Activity, DoorClosed, Boxes,
} from 'lucide-react';
import {
  driversApi, sitesApi, placementApi, edgesApi,
  type DriverCatalogEntry,
  type DiscoveredDevice,
} from '@/lib/inventory-api';

type Step = 'pick' | 'configure' | 'done';

// A configurable field derived from one configSchema property, carrying the
// schema's render hints (label, default, enum, type) so the form looks right
// per-driver automatically — like Commander builds its forms from properties.
interface SchemaField {
  key: string;
  type: string;            // 'string' | 'number' | 'boolean' | 'array' | 'object'
  label: string;
  description?: string;
  options?: string[];      // enum → dropdown
  default?: string;        // default value (string-coerced for the form)
  required: boolean;
  format?: string;         // e.g. 'password'
  advanced: boolean;       // arrays/objects (e.g. channels = auto-discovery) hidden by default
}

// Pretty-print a camelCase/snake key as a fallback label when the schema has no title.
function humanize(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());
}

// Pull the typed field list out of a driver's JSON-schema configSchema
// ({ properties: { host: {...}, port: {...} } }), honoring title/default/enum/type.
function fieldsOf(d: DriverCatalogEntry, t?: TFunction): SchemaField[] {
  const schema = d.configSchema ?? {};
  const props = (schema.properties ?? schema) as Record<string, unknown>;
  const required = new Set((schema.required as string[]) ?? []);
  const out: SchemaField[] = [];
  for (const [key, raw] of Object.entries(props)) {
    if (key === 'type' || key === 'required' || key === 'properties') continue;
    const def = (raw ?? {}) as Record<string, unknown>;
    const fieldType = (def.type as string) ?? 'string';
    const enumVals = Array.isArray(def.enum) ? (def.enum as unknown[]).map(String) : undefined;
    out.push({
      key,
      type: fieldType,
      label: (def.title as string) ?? humanize(key),
      description: def.description as string | undefined,
      options: enumVals,
      default: def.default !== undefined ? String(def.default) : undefined,
      required: required.has(key),
      format: def.format as string | undefined,
      // Array/object fields (e.g. Dahua `channels` = "leave empty for
      // auto-discovery") aren't simple inputs — treat as advanced/auto and
      // hide them so the operator doesn't think they must fill them in.
      advanced: fieldType === 'array' || fieldType === 'object',
    });
  }
  if (out.length === 0 && d.slug !== 'video-demo') {
    return [
      { key: 'host', type: 'string', label: t ? t('addDeviceModal.fields.host') : 'Host', required: true, advanced: false },
      { key: 'port', type: 'number', label: t ? t('addDeviceModal.fields.port') : 'Port', required: false, advanced: false, default: '80' },
      { key: 'username', type: 'string', label: t ? t('addDeviceModal.fields.username') : 'Username', required: true, advanced: false },
      { key: 'password', type: 'string', label: t ? t('addDeviceModal.fields.password') : 'Password', required: true, advanced: false, format: 'password' },
    ];
  }
  return out;
}

// Seed the config form with every field's schema default (so Port=80,
// Protocol=http, EnableEvents=true, PollingInterval=30000 are pre-filled).
function defaultsOf(d: DriverCatalogEntry): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of fieldsOf(d)) {
    if (f.default !== undefined) out[f.key] = f.default;
    else if (f.options && f.options.length > 0) out[f.key] = f.options[0];
  }
  return out;
}

// ── Driver categories ──────────────────────────────────────────────────────
// The catalog ships a `category` per driver ('nvr' | 'vms' | 'camera' |
// 'sensor' | 'access_control' | 'other'). With 30 drivers a flat list is hard
// to scan, so the picker groups by category in a fixed, meaningful order
// (recorders first, then cameras, then everything else) rather than
// alphabetically or in whatever order the API returned.
const CATEGORY_ORDER = ['nvr', 'vms', 'camera', 'sensor', 'access_control', 'other'] as const;

const CATEGORY_ICON: Record<string, typeof Server> = {
  nvr: HardDrive,
  vms: MonitorPlay,
  camera: Video,
  sensor: Activity,
  access_control: DoorClosed,
  other: Boxes,
};

/** Group the catalog by category, in CATEGORY_ORDER; unknown categories last. */
function groupByCategory(
  list: DriverCatalogEntry[],
): Array<{ category: string; drivers: DriverCatalogEntry[] }> {
  const buckets = new Map<string, DriverCatalogEntry[]>();
  for (const d of list) {
    const key = d.category || 'other';
    const arr = buckets.get(key);
    if (arr) arr.push(d);
    else buckets.set(key, [d]);
  }
  const known = CATEGORY_ORDER.filter((c) => buckets.has(c));
  const extra = [...buckets.keys()].filter((c) => !CATEGORY_ORDER.includes(c as never)).sort();
  return [...known, ...extra].map((category) => ({
    category,
    // localeCompare on a missing name would throw during render and trip the
    // ErrorBoundary, blanking the whole Inventory page — coerce instead.
    drivers: (buckets.get(category) ?? []).sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
  }));
}

export function AddDeviceModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const { t } = useTranslation('components');
  const { data: catalog, isLoading: catalogLoading, isError: catalogError, refetch: refetchCatalog } =
    useQuery({ queryKey: ['drivers'], queryFn: driversApi.catalog, retry: 1 });
  const { data: sites } = useQuery({ queryKey: ['sites'], queryFn: sitesApi.list, retry: false });
  // Edges this tenant has provisioned — to optionally run this driver on-site.
  const { data: edges } = useQuery({ queryKey: ['edges'], queryFn: edgesApi.list, retry: false });
  const [step, setStep] = useState<Step>('pick');
  const [driver, setDriver] = useState<DriverCatalogEntry | null>(null);
  const [edgeId, setEdgeId] = useState('');
  const [placeSiteId, setPlaceSiteId] = useState('');
  const [placed, setPlaced] = useState(false);
  const [name, setName] = useState('');
  const [cfg, setCfg] = useState<Record<string, string>>({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [testState, setTestState] = useState<{ ok?: boolean; msg?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [discovered, setDiscovered] = useState<DiscoveredDevice[]>([]);
  // Free-text filter over the driver catalog (name / vendor / category).
  const [query, setQuery] = useState('');
  // Category-first selection: null shows the category tiles, a value shows only
  // that category's drivers. A search query bypasses it and matches across all.
  const [category, setCategory] = useState<string | null>(null);

  // Filter first, then group — so a search narrows the categories shown rather
  // than leaving empty headers behind.
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Every field is optional-guarded: a driver whose manifest omits one (or a
    // future catalog shape) must not throw during render — that trips the
    // ErrorBoundary and takes the whole page down, not just the picker.
    const list = (catalog ?? []).filter((d) => {
      if (!q) return true;
      const hay = [d.name, d.vendor, d.slug, d.category];
      return hay.some((f) => typeof f === 'string' && f.toLowerCase().includes(q));
    });
    return groupByCategory(list);
  }, [catalog, query]);
  const matchCount = useMemo(() => groups.reduce((n, g) => n + g.drivers.length, 0), [groups]);
  // What the list renders: the chosen category, or — while searching — every
  // match flattened into one list so results aren't hidden behind a tile.
  const shownGroup = useMemo(() => {
    if (query.trim()) return { category: '_search', drivers: groups.flatMap((g) => g.drivers) };
    if (!category) return null;
    return groups.find((g) => g.category === category) ?? null;
  }, [groups, category, query]);

  const backToCategories = () => { setCategory(null); setQuery(''); };

  const pick = (d: DriverCatalogEntry) => {
    setDriver(d);
    setName(d.name);
    // Pre-fill the schema defaults so the form opens populated (port 80, http,
    // events on, polling 30000, …) instead of blank.
    setCfg(d.slug === 'video-demo' ? { cameraCount: '4' } : defaultsOf(d));
    setShowAdvanced(false);
    setEdgeId('');
    setTestState(null);
    setStep('configure');
  };

  // Coerce the string form values back to typed config, using the field's
  // declared schema type (boolean toggles → true/false, numbers → number).
  const coerce = (): Record<string, unknown> => {
    const byKey = new Map(driver ? fieldsOf(driver).map((f) => [f.key, f]) : []);
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(cfg)) {
      if (v === '') continue;
      const f = byKey.get(k);
      if (f?.type === 'boolean') out[k] = v === 'true';
      else if (f?.type === 'number') out[k] = Number(v);
      else out[k] = /^\d+$/.test(v) ? Number(v) : v;
    }
    return out;
  };

  const test = async () => {
    if (!driver) return;
    setBusy(true); setError(null);
    try {
      const r = await driversApi.testConnection(driver.slug, coerce());
      setTestState({ ok: r.success, msg: r.note ?? r.error });
    } catch {
      setTestState({ ok: false, msg: t('addDeviceModal.errors.testFailed') });
    } finally { setBusy(false); }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!driver || !name.trim()) { setError(t('addDeviceModal.errors.nameRequired')); return; }
    setBusy(true); setError(null);
    try {
      const { id } = await driversApi.createInstance({ name: name.trim(), driverSlug: driver.slug, config: coerce(), edgeId: edgeId || null });
      const started = await driversApi.start(id);
      if (!started.started) {
        setError(t('addDeviceModal.errors.couldNotConnect'));
        setBusy(false);
        return;
      }
      const disc = await driversApi.discover(id);
      setDiscovered(disc.devices);
      setStep('done');
      onAdded();
    } catch (err: unknown) {
      // The API error envelope varies: `error` is sometimes a string
      // ({error:'unauthorized'}) and sometimes an object ({error:{code,message}}).
      // Reading it blindly rendered "[object Object]" and hid real causes like a
      // 403 role error — pull a human string from whichever shape we got.
      const data = (err as { response?: { data?: unknown } })?.response?.data as
        | { error?: unknown; message?: string }
        | undefined;
      const e = data?.error;
      const msg =
        typeof e === 'string' ? e
        : (e as { message?: string } | undefined)?.message
        ?? data?.message
        ?? (err instanceof Error ? err.message : undefined);
      setError(msg ? t('addDeviceModal.errors.addFailedDetail', { msg }) : t('addDeviceModal.errors.addFailed'));
    } finally { setBusy(false); }
  };

  return (
    <div className="tk-modal-overlay" onClick={onClose}>
      <div className="tk-modal w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">{t('addDeviceModal.title')}</h2>
          <button className="tk-btn-ghost px-2" onClick={onClose} aria-label={t('addDeviceModal.actions.close')}><X size={16} /></button>
        </div>

        {step === 'pick' && (
          <div className="space-y-2">
            <p className="text-sm text-muted">{t('addDeviceModal.pick.prompt')}</p>
            {catalogLoading ? (
              // Loading — a failed/slow catalog fetch used to look identical to
              // "no drivers"; show it explicitly.
              <p className="flex items-center gap-2 py-6 text-sm text-muted">
                <Loader2 size={16} className="animate-spin" /> {t('addDeviceModal.pick.loading')}
              </p>
            ) : catalogError ? (
              // Error (e.g. not signed in → 401) — actionable message + retry,
              // instead of a silent blank list.
              <div className="space-y-2 py-4">
                <p className="flex items-center gap-2 text-sm text-critical">
                  <X size={14} /> {t('addDeviceModal.pick.error')}
                </p>
                <button type="button" className="tk-btn-ghost" onClick={() => refetchCatalog()}>
                  {t('addDeviceModal.actions.retry')}
                </button>
              </div>
            ) : (catalog ?? []).length === 0 ? (
              // Genuinely empty catalog — distinct from a failed load.
              <p className="py-6 text-sm text-muted">{t('addDeviceModal.pick.empty')}</p>
            ) : (
              <>
                {/* Search — with 30+ drivers, scanning the grouped list alone is
                    slow when you already know the vendor you want. */}
                <div className="relative">
                  <Search
                    size={15}
                    className="pointer-events-none absolute inset-inline-start-3 top-1/2 -translate-y-1/2 text-muted"
                  />
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('addDeviceModal.pick.searchPlaceholder')}
                    aria-label={t('addDeviceModal.pick.searchPlaceholder')}
                    className="tk-input w-full ps-9"
                  />
                </div>

                {matchCount === 0 ? (
                  <p className="py-6 text-sm text-muted">
                    {t('addDeviceModal.pick.noMatches', { query: query.trim() })}
                  </p>
                ) : shownGroup ? (
                  /* A category is open (or a search is active) — list just those
                     drivers, with a way back to the tiles. */
                  <div className="space-y-2">
                    {category && !query.trim() && (
                      <button
                        type="button"
                        onClick={backToCategories}
                        className="-ms-1 flex items-center gap-1 rounded px-1 py-0.5 text-xs text-muted hover:text-white"
                      >
                        <ChevronLeft size={14} className="shrink-0 rtl:rotate-180" />
                        {t('addDeviceModal.pick.allCategories')}
                      </button>
                    )}
                    <ul className="space-y-2">
                      {shownGroup.drivers.map((d) => (
                        <li key={d.slug}>
                          <button
                            onClick={() => pick(d)}
                            className="flex w-full items-center gap-3 rounded-lg p-3 text-left ring-1 ring-white/10 hover:bg-white/5"
                          >
                            <Server size={18} className="shrink-0 text-muted" />
                            <span className="min-w-0">
                              <span className="block truncate font-medium">{d.name}</span>
                              <span className="block truncate text-xs text-muted">
                                {d.vendor ? `${d.vendor} · ` : ''}
                                {t('addDeviceModal.pick.capabilities', { count: d.capabilities.length })}
                              </span>
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  /* Default: compact category tiles, not the whole catalog. */
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {groups.map((g) => {
                      const CatIcon = CATEGORY_ICON[g.category] ?? Boxes;
                      return (
                        <button
                          key={g.category}
                          type="button"
                          onClick={() => setCategory(g.category)}
                          className="flex flex-col items-start gap-1.5 rounded-lg p-3 text-left ring-1 ring-white/10 hover:bg-white/5"
                        >
                          <CatIcon size={20} className="shrink-0 text-muted" />
                          <span className="text-sm font-medium leading-tight">
                            {t(`addDeviceModal.pick.categories.${g.category}`, {
                              defaultValue: g.category,
                            })}
                          </span>
                          <span className="text-xs text-muted">
                            {t('addDeviceModal.pick.driverCount', { count: g.drivers.length })}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {step === 'configure' && driver && (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="mb-1 block text-sm text-muted">{t('addDeviceModal.configure.nameLabel')}</label>
              <input
                value={name} onChange={(e) => setName(e.target.value)} autoFocus
                className="tk-input"
              />
            </div>
            {(() => {
              const fields = fieldsOf(driver, t);
              const primary = fields.filter((f) => !f.advanced);
              const advanced = fields.filter((f) => f.advanced);
              const set = (k: string, v: string) => { setCfg((c) => ({ ...c, [k]: v })); setTestState(null); };
              const renderField = (f: SchemaField) => {
                const val = cfg[f.key] ?? '';
                // Array/object fields (e.g. channels) are auto-discovered, not
                // hand-entered — show them as an informational note, not an input
                // that would produce a bad string value.
                if (f.type === 'array' || f.type === 'object') {
                  return (
                    <div key={f.key}>
                      <label className="mb-1 block text-sm text-muted">{f.label}</label>
                      <p className="rounded-lg bg-white/5 px-3 py-2 text-xs text-muted ring-1 ring-white/10">
                        {f.description ? `${t('addDeviceModal.configure.autoDetected')} — ${f.description}` : `${t('addDeviceModal.configure.autoDetected')}.`}
                      </p>
                    </div>
                  );
                }
                return (
                  <div key={f.key}>
                    <label className="mb-1 block text-sm text-muted">
                      {f.label}{f.required && <span className="text-critical"> *</span>}
                    </label>
                    {f.options ? (
                      // enum → dropdown (e.g. Protocol http/https, Auth idp/basic)
                      <select
                        value={val} onChange={(e) => set(f.key, e.target.value)}
                        className="tk-input"
                      >
                        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : f.type === 'boolean' ? (
                      // boolean → toggle (e.g. EnableEvents)
                      <button
                        type="button" role="switch" aria-checked={val === 'true'}
                        onClick={() => set(f.key, val === 'true' ? 'false' : 'true')}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${val === 'true' ? 'bg-primary' : 'bg-white/10 ring-1 ring-white/15'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${val === 'true' ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    ) : (
                      <input
                        type={f.format === 'password' || f.key.toLowerCase().includes('password') ? 'password' : f.type === 'number' ? 'number' : 'text'}
                        value={val}
                        onChange={(e) => set(f.key, e.target.value)}
                        placeholder={f.key === 'host' || f.key.toLowerCase().includes('host') ? '10.0.0.50' : f.default ?? ''}
                        className="tk-input"
                      />
                    )}
                    {f.description && <p className="mt-1 text-xs text-muted/70">{f.description}</p>}
                  </div>
                );
              };
              return (
                <>
                  {primary.map(renderField)}
                  {advanced.length > 0 && (
                    <div>
                      <button
                        type="button" onClick={() => setShowAdvanced((s) => !s)}
                        className="text-xs text-muted hover:text-fg"
                      >
                        {showAdvanced ? '▾' : '▸'} {t('addDeviceModal.configure.advancedToggle', { count: advanced.length })}
                      </button>
                      {showAdvanced && <div className="mt-2 space-y-3">{advanced.map(renderField)}</div>}
                    </div>
                  )}
                </>
              );
            })()}

            {/* Run-on-edge (optional). Default = cloud-direct. Pick an edge to
                poll this device from a customer-site agent (LAN-only devices). */}
            {(edges ?? []).length > 0 && (
              <div>
                <label className="mb-1 flex items-center gap-1.5 text-xs text-muted">
                  <Server size={13} /> {t('addDeviceModal.configure.runOnEdge')}
                </label>
                <select value={edgeId} onChange={(e) => setEdgeId(e.target.value)} className="tk-input w-full">
                  <option value="">{t('addDeviceModal.configure.runOnEdgeCloud')}</option>
                  {(edges ?? []).map((ed) => (
                    <option key={ed.edgeId} value={ed.edgeId}>
                      {ed.name || ed.edgeId}{ed.connected ? '' : ` (${t('addDeviceModal.configure.runOnEdgeOffline')})`}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-muted/70">{t('addDeviceModal.configure.runOnEdgeHint')}</p>
              </div>
            )}

            {testState && (
              <p className={`flex items-center gap-1.5 text-sm ${testState.ok ? 'text-ok' : 'text-critical'}`}>
                {testState.ok ? <Check size={14} /> : <X size={14} />}{testState.msg}
              </p>
            )}
            {error && <p className="text-sm text-critical">{error}</p>}

            <div className="flex items-center justify-between pt-1">
              <button type="button" className="tk-btn-ghost" onClick={() => setStep('pick')} disabled={busy}>{t('addDeviceModal.actions.back')}</button>
              <div className="flex gap-2">
                <button type="button" className="tk-btn-ghost" onClick={test} disabled={busy}>
                  {busy ? <Loader2 size={14} className="animate-spin" /> : t('addDeviceModal.actions.testConnection')}
                </button>
                <button type="submit" className="tk-btn-primary" disabled={busy}>
                  {busy ? t('addDeviceModal.actions.connecting') : t('addDeviceModal.actions.addAndConnect')}
                </button>
              </div>
            </div>
          </form>
        )}

        {step === 'done' && (
          <div className="space-y-3">
            <p className="flex items-center gap-2 text-sm text-ok"><Check size={16} /> {t('addDeviceModal.done.connected', { count: discovered.length })}</p>
            <ul className="max-h-48 space-y-1 overflow-auto text-sm">
              {discovered.map((d) => (
                <li key={d.id} className="flex items-center justify-between rounded px-2 py-1.5 ring-1 ring-white/10">
                  <span className="truncate">{d.name}{d.channel != null ? ` · ${t('addDeviceModal.done.channel', { channel: d.channel })}` : ''}</span>
                  <span className="text-xs text-muted">{d.coreType} · {d.status}</span>
                </li>
              ))}
            </ul>

            {/* Optional: place the new devices at a site now (the parent NVR is
                placed and its channels inherit). Skippable. */}
            {placed ? (
              <p className="flex items-center gap-1.5 text-sm text-ok"><Check size={14} /> {t('addDeviceModal.done.placedConfirm')}</p>
            ) : (
              <div className="rounded-lg p-3 ring-1 ring-white/10">
                <div className="mb-1.5 flex items-center gap-1.5 text-sm font-medium"><MapPin size={14} /> {t('addDeviceModal.done.placeHeading')}</div>
                <p className="mb-2 text-xs text-muted">{t('addDeviceModal.done.placeHint')}</p>
                <div className="flex gap-2">
                  <select
                    value={placeSiteId} onChange={(e) => setPlaceSiteId(e.target.value)}
                    className="tk-input flex-1"
                  >
                    <option value="">{t('addDeviceModal.done.chooseSite')}</option>
                    {(sites ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  <button
                    className="tk-btn-ghost" disabled={!placeSiteId || busy}
                    onClick={async () => {
                      setBusy(true);
                      try {
                        const parents = discovered.filter((d) => !d.parentDeviceId);
                        const targets = parents.length > 0 ? parents : discovered;
                        for (const d of targets) await placementApi.place(d.id, { siteId: placeSiteId });
                        setPlaced(true); onAdded();
                      } finally { setBusy(false); }
                    }}
                  >
                    {busy ? '…' : t('addDeviceModal.actions.place')}
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button className="tk-btn-primary" onClick={onClose}>{t('addDeviceModal.actions.done')}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
